import React, { useMemo } from 'react';
import { DndContext } from '@dnd-kit/core';
import TaskFilters from '../../../../features/filters/components/TaskFilters/TaskFilters';
import TaskList from './TaskList/TaskList';
import FileName from '../../../../features/files/components/FileName/FileName';
import SaveButton from '../../../../ui/components/SaveButton/SaveButton';
import TaskEditToolbar from '../TaskRow/TaskEditToolbar/TaskEditToolbar';
import TaskTableListHeader from './TaskTableListHeader';
import ViewModeSwitch from './ViewModeSwitch';
import { SHOW_TASK_EDIT_TOOLBAR } from '../../../../shared/constants/featureFlags';
import { findTaskInTreeByReactKey, getSubtaskStats, findPreviousSibling } from '../../utils/taskUtils';
import { canCompleteTask } from '../../utils/sequentialTasks';

/**
 * Standard view component for TaskTable
 * Displays tasks in a standard filtered list view
 */
export default function TaskTableStandardView({
  filter,
  timeFilter,
  onFilterChange,
  onTimeFilterChange,
  visiblePrioritizeSections,
  togglePrioritizeSectionVisibility,
  tasks,
  filteredTaskTree,
  currentFileName,
  editingFileName,
  onFileNameClick,
  onFileNameBlur,
  onFileNameKeyDown,
  currentFileId,
  files,
  currentFile,
  hasUnsavedChanges,
  hasRestoredDraft = false,
  loggedOutWorkspaceSnapshot = null,
  sensors,
  collisionDetection,
  onDragOver,
  onDragStart,
  onDragEnd,
  onDragCancel,
  expandedTasks,
  editingTaskId,
  editingDueDateTaskId,
  blockingTasksHighlight,
  editingElementRef,
  editHighlightAnchorRef,
  onEditHighlightAnchorReady,
  dateInputCallbackRef,
  onToggleExpand,
  onCheckboxChange,
  onSetEditingTaskId,
  onSetEditingDueDateTaskId,
  onUpdateTaskDueDate,
  onTaskTextClick,
  onTaskTextFocus,
  onTaskTextInput,
  onTaskContentClick,
  onTaskTextBlur,
  onTaskTextKeyDown,
  onTaskTextPaste,
  onBlockedIconMouseEnter,
  onBlockedIconMouseLeave,
  renderTask,
  dragOverTaskId,
  dropPosition,
  placeholderStatus = 'incomplete',
  placeholderDueDate = null,
  placeholderIsLaterDate = false,
  placeholderHasContent = false,
  onAddTaskAfterRoot,
  onIndent,
  onUnindent,
  onMakeSequential,
  onRemoveSequential,
  onAddSubtask,
  onRequestDeleteTask,
  topToolbarFlag = false,
  isLoggedIn = false
}) {
  const currentFileTasks = useMemo(
    () => tasks.filter(t => String(t.file_id) === String(currentFileId)),
    [tasks, currentFileId]
  );
  const isPlaceholderSelected = tasks.length === 0 && editingTaskId === 'placeholder_client_key';
  const selectedTask = useMemo(
    () => (editingTaskId && filteredTaskTree ? findTaskInTreeByReactKey(filteredTaskTree, editingTaskId) : null),
    [editingTaskId, filteredTaskTree]
  );
  const placeholderTaskForToolbar = useMemo(
    () => ({
      id: 'placeholder',
      _reactKey: 'placeholder_client_key',
      status: placeholderStatus,
      due_date: placeholderDueDate ?? null,
      is_later_date: placeholderIsLaterDate === true,
      subtasks: [],
      parent_task_id: null,
      text: '',
      order: null
    }),
    [placeholderStatus, placeholderDueDate, placeholderIsLaterDate]
  );
  const hasSelection = !!selectedTask || isPlaceholderSelected;
  const dummyTask = useMemo(
    () => ({
      id: '__none__',
      _reactKey: '__none__',
      status: 'incomplete',
      due_date: null,
      subtasks: [],
      parent_task_id: null,
      text: ''
    }),
    []
  );
  const taskForToolbar = hasSelection
    ? (selectedTask || placeholderTaskForToolbar)
    : dummyTask;
  const hasSubtasks = hasSelection && taskForToolbar.subtasks?.length > 0;
  const subtaskStats = hasSubtasks ? getSubtaskStats(taskForToolbar) : null;
  const isComplete = hasSubtasks
    ? (subtaskStats?.total > 0 && subtaskStats?.completed === subtaskStats?.total)
    : (taskForToolbar.status === 'complete' || taskForToolbar.status === 'wont_do');
  const isSequentialDisabled = hasSelection
    ? (!isPlaceholderSelected && !canCompleteTask(taskForToolbar.id, currentFileTasks) && !isComplete)
    : true;
  const isSubtask = hasSelection && !!taskForToolbar.parent_task_id;
  const canBecomeSubtask = hasSelection && !!findPreviousSibling(taskForToolbar.id, currentFileTasks);
  const canUnindent = isSubtask && !!onUnindent;
  const canIndent = canBecomeSubtask && !!onIndent;

  const topHeaderLeftContent = topToolbarFlag ? (
    <TaskTableListHeader
      fileName={currentFileName}
      isEditing={editingFileName}
      onFileNameClick={onFileNameClick}
      onFileNameBlur={onFileNameBlur}
      onFileNameKeyDown={onFileNameKeyDown}
      tasks={tasks}
      currentFileId={currentFileId}
      files={files}
      currentFile={currentFile}
      hasUnsavedChanges={hasUnsavedChanges}
      hasRestoredDraft={hasRestoredDraft}
      loggedOutWorkspaceSnapshot={loggedOutWorkspaceSnapshot}
      editHighlightAnchorRef={editHighlightAnchorRef}
      onEditHighlightAnchorReady={onEditHighlightAnchorReady}
    />
  ) : null;

  const topHeaderToolbarRow = topToolbarFlag && SHOW_TASK_EDIT_TOOLBAR ? (
    <div className="task-view-top-toolbar-inner">
      {!isLoggedIn && (
        <SaveButton
          tasks={tasks}
          currentFileId={currentFileId}
          currentFileName={currentFileName || ''}
          hasUnsavedChanges={hasUnsavedChanges}
          hasRestoredDraft={hasRestoredDraft}
          loggedOutWorkspaceSnapshot={loggedOutWorkspaceSnapshot}
          alwaysVisible
        />
      )}
      {filter !== 'prioritize' && (
        <TaskEditToolbar
          task={taskForToolbar}
          tasks={tasks}
          currentStatus={taskForToolbar.status}
          onStatusSelect={onCheckboxChange}
          hasSubtasks={hasSubtasks}
          subtaskStats={subtaskStats}
          isSequentialDisabled={!hasSelection || isSequentialDisabled}
          canUnindent={canUnindent}
          canIndent={canIndent}
          onUnindent={onUnindent}
          onIndent={onIndent}
          onUpdateTaskDueDate={onUpdateTaskDueDate}
          onFocusAfterDueDateSet={undefined}
          onMakeSequential={onMakeSequential}
          onRemoveSequential={onRemoveSequential}
          onAddSubtask={onAddSubtask}
          onRequestDeleteTask={onRequestDeleteTask}
          filter={filter}
          topToolbar
          showLabels
          disabled={!hasSelection}
        />
      )}
      <ViewModeSwitch filter={filter} onFilterChange={onFilterChange} />
    </div>
  ) : null;

  return (
    <>
      <TaskFilters
        filter={filter}
        timeFilter={timeFilter}
        onFilterChange={onFilterChange}
        onTimeFilterChange={onTimeFilterChange}
        visiblePrioritizeSections={visiblePrioritizeSections}
        onTogglePrioritizeSectionVisibility={togglePrioritizeSectionVisibility}
        tasks={tasks}
        leftContent={topHeaderLeftContent}
        toolbarRow={topHeaderToolbarRow}
        modeSwitchInToolbar={topToolbarFlag}
      />
      <div className="task-table-content">
      <DndContext
        sensors={sensors}
        collisionDetection={collisionDetection}
        onDragOver={onDragOver}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onDragCancel={onDragCancel}
      >
        <div className="task-list-container">
          {!topToolbarFlag && currentFileName && (
            <div className="task-view-filename-row">
              <FileName
                fileName={currentFileName}
                isEditing={editingFileName}
                onFileNameClick={onFileNameClick}
                onFileNameBlur={onFileNameBlur}
                onFileNameKeyDown={onFileNameKeyDown}
                tasks={tasks}
                currentFileId={currentFileId}
                files={files}
                currentFile={currentFile}
                hasUnsavedChanges={hasUnsavedChanges}
                hasRestoredDraft={hasRestoredDraft}
                loggedOutWorkspaceSnapshot={loggedOutWorkspaceSnapshot}
                editHighlightAnchorRef={editHighlightAnchorRef}
                onEditHighlightAnchorReady={onEditHighlightAnchorReady}
              />
              <div className="task-view-logo-wrapper">
                <SaveButton
                  tasks={tasks}
                  currentFileId={currentFileId}
                  currentFileName={currentFileName}
                  hasUnsavedChanges={hasUnsavedChanges}
                  hasRestoredDraft={hasRestoredDraft}
                  loggedOutWorkspaceSnapshot={loggedOutWorkspaceSnapshot}
                />
                <div className="task-view-logo" aria-hidden="true">
                  <svg width="130" height="40" viewBox="0 0 130 40" xmlns="http://www.w3.org/2000/svg">
                    <text x="10" y="28" fontFamily="system-ui, -apple-system, sans-serif" fontSize="24" fontWeight="600">
                      <tspan fill="#444" fontSize="24">pico</tspan><tspan fill="#333" fontSize="28" dx="1">todo</tspan>
                    </text>
                  </svg>
                  <span className="task-view-logo-badge">BETA</span>
                </div>
              </div>
            </div>
          )}
          <TaskList
            tasks={filteredTaskTree}
            expandedTasks={expandedTasks}
            editingTaskId={editingTaskId}
            editingDueDateTaskId={editingDueDateTaskId}
            blockingTasksHighlight={blockingTasksHighlight}
            filter={filter}
            timeFilter={timeFilter}
            editingElementRef={editingElementRef}
            dateInputCallbackRef={dateInputCallbackRef}
            onToggleExpand={onToggleExpand}
            onCheckboxChange={onCheckboxChange}
            onSetEditingTaskId={onSetEditingTaskId}
            onSetEditingDueDateTaskId={onSetEditingDueDateTaskId}
            onUpdateTaskDueDate={onUpdateTaskDueDate}
            onTaskTextClick={onTaskTextClick}
            onTaskTextFocus={onTaskTextFocus}
            onTaskTextInput={onTaskTextInput}
            onTaskContentClick={onTaskContentClick}
            onTaskTextBlur={onTaskTextBlur}
            onTaskTextKeyDown={onTaskTextKeyDown}
            onTaskTextPaste={onTaskTextPaste}
            onBlockedIconMouseEnter={onBlockedIconMouseEnter}
            onBlockedIconMouseLeave={onBlockedIconMouseLeave}
            renderTask={renderTask}
            dragOverTaskId={dragOverTaskId}
            dropPosition={dropPosition}
            placeholderStatus={placeholderStatus}
            placeholderDueDate={placeholderDueDate}
            placeholderIsLaterDate={placeholderIsLaterDate}
            placeholderHasContent={placeholderHasContent}
            onAddTaskAfterRoot={onAddTaskAfterRoot}
          />
        </div>
      </DndContext>
      </div>
    </>
  );
}
